import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import AssessmentIcon from '@mui/icons-material/Assessment'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import InventoryIcon from '@mui/icons-material/Inventory'
import {
  ClientMappingHistoryAnalyticsRow,
  CurrentVehicleStatusAnalyticsRow,
  OverallVehicleStatusAnalyticsRow,
  exportClientMappingHistoryAnalytics,
  exportCurrentVehicleStatusAnalytics,
  exportOverallVehicleStatusAnalytics,
  fetchClientMappingHistoryAnalytics,
  fetchCurrentVehicleStatusAnalytics,
  fetchOverallVehicleStatusAnalytics,
} from '../services/vehicleHistoryService'

const formatDate = (value: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-IN')
}

const StatCard = ({
  title,
  value,
  helper,
  icon,
  color,
}: {
  title: string
  value: number
  helper: string
  icon: React.ReactNode
  color: string
}) => (
  <Card
    sx={{
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.88)' : '#ffffff',
      border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.12)'}`,
      borderRadius: 2,
      minHeight: 116,
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? 'none'
        : '0 16px 34px rgba(15, 23, 42, 0.10)',
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Box>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.74)' : '#475569', fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ color, fontWeight: 900, mt: 0.5 }}>
          {value.toLocaleString('en-IN')}
        </Typography>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.68)' : '#64748b', mt: 0.5 }}>
          {helper}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}22`,
          color,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {icon}
      </Box>
    </CardContent>
  </Card>
)

const RiderVehicleAnalytics: React.FC = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const surfaceBg = isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff'
  const tableBg = isDark ? 'rgba(15, 23, 42, 0.92)' : '#ffffff'
  const tableHeaderBg = isDark ? '#0f172a' : '#eef4ff'
  const borderColor = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.12)'
  const rowBorderColor = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.09)'
  const lightShadow = '0 18px 42px rgba(15, 23, 42, 0.10)'
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? 'rgba(15, 23, 42, 0.52)' : '#ffffff',
      '& fieldset': {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.34)' : 'rgba(15, 23, 42, 0.22)',
      },
      '&:hover fieldset': {
        borderColor: isDark ? 'rgba(96, 165, 250, 0.55)' : 'rgba(37, 99, 235, 0.42)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(226, 232, 240, 0.72)' : '#334155',
    },
    '& .MuiInputBase-input, & .MuiSelect-select': {
      color: isDark ? '#e5edf8' : '#0f172a',
      fontWeight: 700,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: isDark ? 'rgba(226, 232, 240, 0.72)' : '#475569',
    },
  }
  const [reportMode, setReportMode] = useState<'current' | 'overall' | 'clientMapping'>('current')
  const [rows, setRows] = useState<Array<CurrentVehicleStatusAnalyticsRow | OverallVehicleStatusAnalyticsRow | ClientMappingHistoryAnalyticsRow>>([])
  const [cities, setCities] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [city, setCity] = useState('')
  const [status, setStatus] = useState('')
  const [summary, setSummary] = useState({ total: 0, deployed: 0, returned: 0, yetNotDeployed: 0 })
  const tableColumnCount = reportMode === 'clientMapping' ? 7 : 13

  const loadCurrentStatus = useCallback(async (targetPage: number, targetLimit: number) => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        limit: targetLimit,
        offset: targetPage * targetLimit,
        search: search.trim() || undefined,
        city: city || undefined,
        status: status || undefined,
      }
      const response = reportMode === 'current'
        ? await fetchCurrentVehicleStatusAnalytics(params)
        : reportMode === 'overall'
          ? await fetchOverallVehicleStatusAnalytics(params)
          : await fetchClientMappingHistoryAnalytics(params)

      if (!response.success || !response.data?.success) {
        throw new Error(response.message || response.data?.message || 'Failed to load rider vehicle analytics')
      }

      setRows(Array.isArray(response.data.data) ? response.data.data : [])
      setTotal(response.data.pagination?.total || 0)
      setCities(Array.isArray(response.data.filters?.cities) ? response.data.filters.cities : [])
      setSummary({
        total: response.data.summary?.total || response.data.pagination?.total || 0,
        deployed: response.data.summary?.deployed || 0,
        returned: response.data.summary?.returned || 0,
        yetNotDeployed: response.data.summary?.yetNotDeployed || 0,
      })
    } catch (err: any) {
      setRows([])
      setTotal(0)
      setSummary({ total: 0, deployed: 0, returned: 0, yetNotDeployed: 0 })
      setError(err.message || 'Failed to load rider vehicle analytics')
    } finally {
      setLoading(false)
    }
  }, [city, reportMode, search, status])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadCurrentStatus(0, rowsPerPage)
      setPage(0)
    }, 350)

    return () => window.clearTimeout(timer)
  }, [city, reportMode, rowsPerPage, search, status, loadCurrentStatus])

  const handleExport = async () => {
    try {
      setExporting(true)
      setError(null)
      const params = {
        search: search.trim() || undefined,
        city: city || undefined,
        status: status || undefined,
      }
      const response = reportMode === 'current'
        ? await exportCurrentVehicleStatusAnalytics(params)
        : reportMode === 'overall'
          ? await exportOverallVehicleStatusAnalytics(params)
          : await exportClientMappingHistoryAnalytics(params)

      if (!response.success || !response.data?.blob) {
        throw new Error(response.message || 'Export failed')
      }

      const url = URL.createObjectURL(response.data.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = response.data.filename || 'current-vehicle-status.xlsx'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setSuccess(
        reportMode === 'current'
          ? 'Current vehicle status exported successfully'
          : reportMode === 'overall'
            ? 'Overall vehicle status exported successfully'
            : 'Client mapping history exported successfully'
      )
    } catch (err: any) {
      setError(err.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, color: isDark ? '#e5edf8' : '#0f172a' }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box>
          <Chip
            icon={<AssessmentIcon />}
            label="Rider Service"
            sx={{
              color: '#dbeafe',
              bgcolor: isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(37, 99, 235, 0.12)',
              border: `1px solid ${isDark ? 'rgba(96, 165, 250, 0.4)' : 'rgba(37, 99, 235, 0.26)'}`,
              '& .MuiChip-label, & .MuiChip-icon': {
                color: isDark ? '#dbeafe' : '#1d4ed8',
              },
              fontWeight: 800,
              mb: 1,
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: 0, fontSize: { xs: 32, md: 44 } }}>
            Rider Vehicle Analytics
          </Typography>
          <Typography sx={{ color: isDark ? 'rgba(226, 232, 240, 0.72)' : '#475569', mt: 0.5 }}>
            Current and overall rider vehicle status reports from vehicle history.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadCurrentStatus(page, rowsPerPage)}
            disabled={loading}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </Box>
      </Box>

      {reportMode === 'current' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <StatCard
            title="Filtered Records"
            value={summary.total}
            helper="Latest rider vehicle rows"
            icon={<TwoWheelerIcon />}
            color="#3b82f6"
          />
          <StatCard
            title="Deployed"
            value={summary.deployed}
            helper="Filtered deployed rows"
            icon={<AssignmentTurnedInIcon />}
            color="#22c55e"
          />
          <StatCard
            title="Returned"
            value={summary.returned}
            helper="Filtered returned rows"
            icon={<KeyboardReturnIcon />}
            color="#f97316"
          />
          <StatCard
            title="Yet notdeployed"
            value={summary.yetNotDeployed}
            helper="Filtered hub stock rows"
            icon={<InventoryIcon />}
            color="#38bdf8"
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          mb: 3,
          bgcolor: surfaceBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
          boxShadow: isDark ? 'none' : lightShadow,
        }}
      >
        <CardContent sx={{ pb: 0 }}>
          <Tabs
            value={reportMode}
            onChange={(_event, value) => {
              setReportMode(value)
              setPage(0)
              setStatus('')
            }}
            sx={{ mb: 2 }}
          >
            <Tab value="current" label="Current Vehicle Status" />
            <Tab value="overall" label="Overall Vehicle Status" />
            <Tab value="clientMapping" label="Client Mapping History" />
          </Tabs>
        </CardContent>
        <CardContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: reportMode === 'clientMapping' ? '2fr 1fr' : '2fr 1fr 1fr' }, gap: 2, pt: 0 }}>
          <TextField
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={reportMode === 'clientMapping' ? 'EV91 ID, client ID, source, phone' : 'Vehicle number, rider ID, name, phone'}
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={fieldSx}>
            <InputLabel>City</InputLabel>
            <Select label="City" value={city} onChange={(event) => setCity(event.target.value)}>
              <MenuItem value="">All Cities</MenuItem>
              {cities.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {reportMode !== 'clientMapping' && (
            <FormControl sx={fieldSx}>
              <InputLabel>Current Status</InputLabel>
              <Select label="Current Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="DEPLOYED">Deployed</MenuItem>
                <MenuItem value="RETURNED">{reportMode === 'current' ? 'Return' : 'Returned'}</MenuItem>
                {reportMode === 'overall' && <MenuItem value="CLIENT_SWAP">Client-Swap</MenuItem>}
                {reportMode === 'current' && <MenuItem value="YET_NOT_DEPLOYED">Yet notdeployed</MenuItem>}
              </Select>
            </FormControl>
          )}
        </CardContent>
      </Card>

      <Card
        sx={{
          bgcolor: tableBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: isDark ? 'none' : lightShadow,
        }}
      >
        <TableContainer sx={{ maxHeight: 'calc(100vh - 430px)', minHeight: 420, bgcolor: tableBg }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {(reportMode === 'current'
                  ? [
                      'City',
                      'Vehicle Number',
                      'EV91 Rider ID',
                      'Rider ID',
                      'Rider name',
                      'Rider Contact',
                      'Current Status (VEHICLE)',
                      'Reason',
                      'Last status Date',
                      'Client Name',
                      'Aging',
                      'Operational Status',
                      'Source_Name',
                    ]
                  : reportMode === 'overall'
                    ? [
                      'city_name',
                      'Vehicle Number',
                      'EV91_Rider_ID',
                      'Rider_Name',
                      'Rider_Contact_Number',
                      'Client ID',
                      'Client Name',
                      'vehicle_status',
                      'Reason',
                      'date',
                      'Aging',
                      'Source_Name',
                      'SD_Amount',
                    ]
                    : [
                      'City',
                      'EV91 ID',
                      'Client ID',
                      'ClientName',
                      'Source',
                      'Last Update',
                      'Phone No',
                    ]).map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                        bgcolor: tableHeaderBg,
                        color: isDark ? '#f8fafc' : '#0f172a',
                        borderBottom: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.24)' : 'rgba(15, 23, 42, 0.14)'}`,
                      }}
                    >
                      {heading}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 7 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 7 }}>
                    <Typography color="text.secondary">No rider vehicle analytics data found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  if (reportMode === 'clientMapping') {
                    const mappingRow = row as ClientMappingHistoryAnalyticsRow
                    return (
                      <TableRow
                        key={`${mappingRow.ev91RiderId}-${mappingRow.clientId}-${mappingRow.lastUpdated}-${index}`}
                        hover
                        sx={{
                          '& td': {
                            bgcolor: isDark ? 'transparent' : '#ffffff',
                            color: isDark ? '#e5edf8' : '#172033',
                            borderBottom: `1px solid ${rowBorderColor}`,
                          },
                          '&:hover td': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.08)' : alpha(theme.palette.primary.main, 0.055),
                          },
                        }}
                      >
                        <TableCell>{mappingRow.city || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{mappingRow.ev91RiderId || '-'}</TableCell>
                        <TableCell>{mappingRow.clientId || '-'}</TableCell>
                        <TableCell>{mappingRow.clientName || '-'}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{mappingRow.source || '-'}</TableCell>
                        <TableCell>{formatDate(mappingRow.lastUpdated)}</TableCell>
                        <TableCell>{mappingRow.phoneNumber || '-'}</TableCell>
                      </TableRow>
                    )
                  }

                  if (reportMode === 'overall') {
                    const overallRow = row as OverallVehicleStatusAnalyticsRow
                    return (
                      <TableRow
                        key={`${overallRow.ev91RiderId}-${overallRow.vehicleNumber}-${overallRow.statusDate}-${index}`}
                        hover
                        sx={{
                          '& td': {
                            bgcolor: isDark ? 'transparent' : '#ffffff',
                            color: isDark ? '#e5edf8' : '#172033',
                            borderBottom: `1px solid ${rowBorderColor}`,
                          },
                          '&:hover td': {
                            bgcolor: isDark ? 'rgba(59, 130, 246, 0.08)' : alpha(theme.palette.primary.main, 0.055),
                          },
                        }}
                      >
                        <TableCell>{overallRow.cityName || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{overallRow.vehicleNumber || '-'}</TableCell>
                        <TableCell>{overallRow.ev91RiderId || '-'}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{overallRow.riderName || '-'}</TableCell>
                        <TableCell>{overallRow.riderContact || '-'}</TableCell>
                        <TableCell>{overallRow.clientId || '-'}</TableCell>
                        <TableCell>{overallRow.clientName || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={overallRow.vehicleStatus || '-'}
                            color={
                              overallRow.vehicleStatus === 'Deployed'
                                ? 'success'
                                : overallRow.vehicleStatus === 'Client-Swap'
                                  ? 'info'
                                  : 'warning'
                            }
                            size="small"
                            sx={{ fontWeight: 800 }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{overallRow.reason || '-'}</TableCell>
                        <TableCell>{formatDate(overallRow.statusDate)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{Number(overallRow.aging || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{overallRow.sourceName || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                          {Number(overallRow.sdAmount || 0).toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    )
                  }

                  const currentRow = row as CurrentVehicleStatusAnalyticsRow
                  return (
                    <TableRow
                      key={`${currentRow.ev91RiderId}-${currentRow.vehicleNumber}-${index}`}
                      hover
                      sx={{
                        '& td': {
                          bgcolor: isDark ? 'transparent' : '#ffffff',
                          color: isDark ? '#e5edf8' : '#172033',
                          borderBottom: `1px solid ${rowBorderColor}`,
                        },
                        '&:hover td': {
                          bgcolor: isDark ? 'rgba(59, 130, 246, 0.08)' : alpha(theme.palette.primary.main, 0.055),
                        },
                      }}
                    >
                      <TableCell>{currentRow.city || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{currentRow.vehicleNumber || '-'}</TableCell>
                      <TableCell>{currentRow.ev91RiderId || '-'}</TableCell>
                      <TableCell>{currentRow.clientRiderId || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>{currentRow.riderName || '-'}</TableCell>
                      <TableCell>{currentRow.riderContact || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={currentRow.currentStatus || '-'}
                          color={
                            currentRow.currentStatus === 'Deployed'
                              ? 'success'
                              : currentRow.currentStatus === 'Yet notdeployed'
                                ? 'info'
                                : 'warning'
                          }
                          size="small"
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{currentRow.reason || '-'}</TableCell>
                      <TableCell>{formatDate(currentRow.lastStatusDate)}</TableCell>
                      <TableCell>{currentRow.clientName || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{Number(currentRow.aging || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>{currentRow.operationalStatus || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{currentRow.source || '-'}</TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          sx={{
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.94)' : '#f8fafc',
            color: isDark ? '#e5edf8' : '#172033',
            borderTop: `1px solid ${borderColor}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: isDark ? 'rgba(226, 232, 240, 0.72)' : '#475569',
              fontWeight: 700,
            },
          }}
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_event, nextPage) => {
            setPage(nextPage)
            loadCurrentStatus(nextPage, rowsPerPage)
          }}
          onRowsPerPageChange={(event) => {
            const nextLimit = Number(event.target.value)
            setRowsPerPage(nextLimit)
            setPage(0)
          }}
        />
      </Card>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        message={success || ''}
      />
    </Box>
  )
}

export default RiderVehicleAnalytics
